import { PieChart, QuestionTable, StackedChart } from "components";
import { Question, ScoreData } from "entities";
import { DateTime } from "luxon";
import React, { useEffect, useRef, useState } from "react";

import { useParams } from "react-router-dom";
import {
  Container,
  Dimmer,
  Header,
  Loader,
  Segment,
  SegmentGroup,
  Icon,
  Message,
  MessageHeader,
  Grid,
  GridColumn,
  Button,
} from "semantic-ui-react";
import { AppService } from "services";
import generatePDF, { Options } from "react-to-pdf";

export const DetailsQuestion: React.FC = () => {
  let { questionid } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [question, setQuestion] = useState<Question | null>(null);
  const [scoreData, setScoreData] = useState<ScoreData[]>([]);
  const [name, setName] = useState("");

  const [print, setPrint] = useState(false);

  const [totalSat, setTotalSat] = useState({
    score1: 0,
    score2: 0,
    score3: 0,
    score4: 0,
    total: 0,
  });

  const ref = useRef<HTMLDivElement>();

  const options: Options = {
    filename: "Export.pdf",
    page: {
      margin: {
        top: 70,
        right: 10,
        bottom: 0,
        left: 10,
      },
    },
  };

  const downloadPdf = () => {
    setPrint(true);
    setTimeout(() => {
      generatePDF(() => ref.current, options);
      setPrint(false);
    }, 1000);
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      const q = await AppService.listQuestionStat(questionid);
      setLoading(false);
      if (!q) setError(true);
      setQuestion(q?.question ?? null);
      setScoreData(
        (q?.stats ?? []).map((score) => ({
          ...score,
          total: score.score1 + score.score2 + score.score3 + score.score4,
        }))
      );
      setName(q?.name ?? "");

      setTotalSat(
        (q?.stats ?? []).reduce(
          (prev, acc) => {
            return {
              score1: acc.score1 + prev.score1,
              score2: acc.score2 + prev.score2,
              score3: acc.score3 + prev.score3,
              score4: acc.score4 + prev.score4,
              total:
                prev.total + acc.score1 + acc.score2 + acc.score3 + acc.score4,
            };
          },
          {
            score1: 0,
            score2: 0,
            score3: 0,
            score4: 0,
            total: 0,
          }
        )
      );
    })();
  }, []);

  if (loading) {
    return (
      <Dimmer active inverted>
        <Loader active inline="centered" />
      </Dimmer>
    );
  }

  return (
    <>
      {error && (
        <Message color="red">
          <MessageHeader>Une erreur est survenu</MessageHeader>
          <p>Veuillez rafraichir cette page</p>
        </Message>
      )}
      {!question && (
        <Segment placeholder>
          <Header icon>
            <Icon name="ban" />
            Question introuvable
          </Header>
        </Segment>
      )}
      {question && (
        <Container style={{ marginBottom: "4rem" }}>
          <SegmentGroup raised>
            <SegmentGroup horizontal>
              <Segment>
                <Header as="h3" style={{ marginTop: 8 }}>
                  {name}
                </Header>
              </Segment>
              <Segment
                style={{ display: "flex", justifyContent: "space-between" }}
              >
                <div>
                  <Header as="h3">ID Borne</Header>
                  {question?.dev_id}
                </div>
                <Button onClick={downloadPdf} loading={print}>
                  <Icon name="file pdf outline" />
                  Exporter PDF
                </Button>
              </Segment>
            </SegmentGroup>

            <Segment>
              <Header as="h3">Question</Header>
              {question?.question}
            </Segment>
            <SegmentGroup horizontal>
              <Segment>
                <Header as="h4">Date de debut</Header>
                {DateTime.fromISO(question?.date_debut).toLocaleString(
                  DateTime.DATE_FULL
                )}
              </Segment>
              <Segment>
                <Header as="h4">Date de fin</Header>
                {DateTime.fromISO(question?.date_fin).toLocaleString(
                  DateTime.DATE_FULL
                )}
              </Segment>
            </SegmentGroup>
          </SegmentGroup>
          <Grid columns={16}>
            <GridColumn computer={10} mobile={16}>
              <PieChart data={scoreData} />
            </GridColumn>
            <GridColumn computer={6} mobile={16}>
              <Segment
                style={{
                  height: "100%",
                }}
              >
                <Header as="h3">Total réponses</Header>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                    paddingBottom: "20px",
                    minHeight: 200,
                  }}
                >
                  <Header as="h2" textAlign="center">
                    <span style={{ fontSize: "80px" }}>
                      {scoreData.reduce(
                        (prev, curr) => (prev += curr.total),
                        0
                      )}
                    </span>
                  </Header>
                </div>
              </Segment>
            </GridColumn>
          </Grid>
          <StackedChart data={scoreData} />
          <QuestionTable data={scoreData} percent />
          <QuestionTable data={scoreData} />
        </Container>
      )}

      <div ref={ref} style={{ display: `${print ? "block" : "none"}` }}>
        <SegmentGroup raised>
          <SegmentGroup horizontal>
            <Segment>
              <Header as="h2" style={{ marginTop: 20 }}>
                {name}
              </Header>
            </Segment>
            <Segment style={{ fontSize: 25 }}>
              <Header as="h2">ID Borne</Header>
              {question?.dev_id}
            </Segment>
          </SegmentGroup>

          <Segment style={{ fontSize: 25 }}>
            <Header as="h2">Question</Header>
            {question?.question}
          </Segment>
          <SegmentGroup horizontal>
            <Segment style={{ fontSize: 25 }}>
              <Header as="h2">Date de debut</Header>
              {DateTime.fromISO(question?.date_debut).toLocaleString(
                DateTime.DATE_FULL
              )}
            </Segment>
            <Segment style={{ fontSize: 25 }}>
              <Header as="h2">Date de fin</Header>
              {DateTime.fromISO(question?.date_fin).toLocaleString(
                DateTime.DATE_FULL
              )}
            </Segment>
            <Segment style={{ fontSize: 25 }}>
              <Header as="h2">Total</Header>
              {totalSat.total}
            </Segment>
          </SegmentGroup>
        </SegmentGroup>
        <PieChart
          data={scoreData}
          title={false}
          width={900}
          labelSize="30px"
          legendSize="30px"
        />
      </div>
    </>
  );
};
